import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/app/providers/AppContextProvider';
import type { SettingType } from '@/types/app';
import SummaryCard from '../SummaryCard';
import { COVERAGE_FIELDS, hasRealCurrentPolicy } from './constants';

interface SummaryProps {
	activeSetting: SettingType | null;
	handleSettingClick: (setting: SettingType) => void;
	loading: boolean;
	idx: number;
	isConfirmed?: boolean;
}

const toComparableNumber = (value: unknown): number | null => {
	const numberValue = Number(value);
	return Number.isFinite(numberValue) ? numberValue : null;
};

const Summary = ({
	activeSetting,
	handleSettingClick,
	loading,
	idx,
	isConfirmed = false
}: SummaryProps) => {
	const { t } = useTranslation();
	const { appData, pendingPremiumVars } = useAppContext();

	// Get coverage source from premium_variables
	const coverageSource = useMemo(() => {
		return appData?.premium_variables?.coverage?.sick_leave_short_term?.source || null;
	}, [appData?.premium_variables?.coverage?.sick_leave_short_term?.source]);

	// Check if coverage values have actually been modified from the detected policy
	const isModified = useMemo(() => {
		// Prefer pending (unsaved) values when available for real-time feedback
		const currentValues =
			pendingPremiumVars.coverageValues ||
			appData?.premium_variables?.coverage?.sick_leave_short_term;
		// Get the original detected values from current_policy coverage
		const detectedValues = appData?.current_policy?.sick_leave_short_term?.coverage;

		// If we don't have both sets of values, we can't determine if modified
		if (!currentValues || !detectedValues) {
			return false;
		}

		return COVERAGE_FIELDS.some((field) => {
			const currentVal = toComparableNumber(currentValues[field]);
			const detectedVal = toComparableNumber(detectedValues[field]);
			if (currentVal === null && detectedVal === null) {
				return false;
			}
			return currentVal !== detectedVal;
		});
	}, [
		appData?.premium_variables?.coverage?.sick_leave_short_term,
		appData?.current_policy?.sick_leave_short_term?.coverage,
		pendingPremiumVars.coverageValues
	]);

	// Status rules (see #963 review):
	// - Coverage detected from an uploaded policy → "Automatisch herkend" / "Aangepast"
	//   (isModified). This is the only case that may claim auto-detection.
	// - No detected coverage, still on the default fallback (source "default") → "Te bevestigen".
	// - No detected coverage, confirmed by the client (source "client", or optimistically
	//   confirmed in the wizard) → "Bevestigd".
	const hasDetectedCoverage = hasRealCurrentPolicy(
		appData?.current_policy?.sick_leave_short_term?.coverage
	);
	let statusText: string;
	if (hasDetectedCoverage) {
		statusText = isModified
			? t('quotes.settings.coverage.modified')
			: t('quotes.settings.coverage.sameAsCurrent');
	} else if (coverageSource === 'default' && !isConfirmed) {
		statusText = t('quotes.settings.coverage.toConfirm');
	} else {
		statusText = t('quotes.settings.coverage.confirmed');
	}

	return (
		<SummaryCard
			type="coverage"
			title={t('quotes.settings.coverage.title')}
			onClick={handleSettingClick}
			isActive={activeSetting === 'coverage'}
			source={coverageSource}
			loading={loading}
			idx={idx}
			isConfirmed={isConfirmed}
		>
			<div
				className="font-bold text-base sm:text-sm text-nowrap"
				data-testid="coverage-summary-status-text"
			>
				{statusText}
			</div>
		</SummaryCard>
	);
};

export default Summary;
